<template>
  <div class="min-h-screen bg-theme-bg2 flex items-center justify-center p-4">

    <div class="bg-theme-card border border-theme-border rounded-2xl w-full max-w-2xl overflow-hidden">

      <!-- Progress bar -->
      <div class="h-1.5 bg-theme-bg2">
        <div
          class="h-1.5 bg-primary-600 transition-all duration-700 ease-out"
          :style="{ width: `${(Math.min(step, TOTAL_STEPS) / TOTAL_STEPS) * 100}%` }"
        />
      </div>

      <div class="p-6 sm:p-8">

        <!-- Step dots -->
        <div v-if="step <= TOTAL_STEPS" class="flex items-center justify-center gap-2 mb-8">
          <template v-for="i in TOTAL_STEPS" :key="i">
            <div :class="[
              'rounded-full transition-all duration-300',
              i === step ? 'w-8 h-2.5 bg-primary-600' :
              i < step   ? 'w-2.5 h-2.5 bg-primary-400' :
                           'w-2.5 h-2.5 bg-theme-border'
            ]" />
          </template>
        </div>

        <!-- Back button -->
        <button
          v-if="step > 1 && step <= TOTAL_STEPS"
          @click="step--"
          class="flex items-center gap-1.5 text-sm text-theme-muted hover:text-primary-600 mb-7 transition font-medium"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m15 18-6-6 6-6"/></svg>
          Back
        </button>

        <!-- ─── Step 1: Role ─── -->
        <div v-if="step === 1">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-theme-text mb-1">How will you use Usenlease?</h2>
            <p class="text-sm text-theme-muted">Choose your primary role. You can adjust this later.</p>
          </div>

            <div class="grid grid-cols-2 gap-4 mb-8">
            <button
              v-for="r in roles" :key="r.value"
              type="button"
              @click="form.role = r.value"
              :class="[
                'flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-200 text-center',
                form.role === r.value
                  ? 'border-primary-600 bg-primary-600/10'
                  : 'border-theme-border bg-theme-bg2 hover:border-primary-400'
              ]"
            >
              <span class="text-4xl">{{ r.icon }}</span>
              <div>
                <p class="font-bold text-theme-text text-base mb-1">{{ r.label }}</p>
                <p class="text-sm text-theme-muted leading-snug">{{ r.description }}</p>
              </div>
              <div :class="[
                'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                form.role === r.value ? 'border-primary-600 bg-primary-600' : 'border-theme-border'
              ]">
                <svg v-if="form.role === r.value" class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M20 6L9 17l-5-5 1.4-1.4L9 14.2 18.6 4.6z"/></svg>
              </div>
            </button>
          </div>

          <button @click="nextStep" :disabled="!form.role" :class="btnClass(!!form.role)">
            Continue
            <svg class="w-4 h-4 inline ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>

        <!-- ─── Step 2: Personal Info ─── -->
        <div v-else-if="step === 2" class="space-y-5">
          <div>
            <h2 class="text-2xl font-bold text-theme-text">Tell us about yourself</h2>
            <p class="text-sm text-theme-muted mt-1">Just the basics — you can fill in more from your profile later.</p>
          </div>

          <!-- Photo + Names -->
          <div class="flex items-center gap-4">
            <label class="cursor-pointer flex-shrink-0 flex flex-col items-center gap-1">
              <div class="w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center border-2 border-dashed border-theme-border bg-theme-bg2 hover:border-primary-500 transition group">
                <img v-if="photoPreview" :src="photoPreview" class="w-full h-full object-cover" />
                <svg v-else class="w-7 h-7 text-theme-muted group-hover:text-primary-500 transition" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <span class="text-xs font-semibold text-primary-600">{{ photoPreview ? 'Change' : 'Photo' }}</span>
              <input type="file" accept="image/jpeg,image/png" class="hidden" @change="onPhotoChange" />
            </label>
            <div class="flex-1 grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-theme-text2 mb-1.5">First name *</label>
                <input v-model="form.first_name" type="text" placeholder="John" :class="inputClass()" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-theme-text2 mb-1.5">Last name *</label>
                <input v-model="form.last_name" type="text" placeholder="Doe" :class="inputClass()" />
              </div>
            </div>
          </div>

          <!-- Phone -->
          <div>
            <label class="block text-xs font-semibold text-theme-text2 mb-1.5">Phone number *</label>
            <vue-tel-input
              v-model="form.phone_number"
              mode="international"
              :inputOptions="{ placeholder: 'Phone number', name: 'phone' }"
              :dropdownOptions="{ showSearchBox: true, showFlags: true, showDialCodeInList: true }"
              @on-input="onPhoneInput"
              class="tel-input-custom"
            />
            <p v-if="phoneError" class="text-red-500 text-xs mt-1 flex items-center gap-1">
              <svg class="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
              {{ phoneError }}
            </p>
          </div>

          <!-- Username -->
          <div>
            <label class="block text-xs font-semibold text-theme-text2 mb-1.5">Username</label>
            <input v-model="form.username" type="text" placeholder="@username" :class="inputClass()" />
          </div>

          <button @click="nextStep" :disabled="!isStep2Valid || saving" :class="btnClass(isStep2Valid && !saving)">
            <i v-if="saving" class="pi pi-spinner pi-spin mr-2"></i>
            {{ saving ? 'Saving...' : 'Continue' }}
            <svg v-if="!saving" class="w-4 h-4 inline ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>

        <!-- ─── Step 3: Location & Business ─── -->
        <div v-else-if="step === 3" class="space-y-5">
          <div>
            <h2 class="text-2xl font-bold text-theme-text">Location & Business</h2>
            <p class="text-sm text-theme-muted mt-1">Help renters find equipment near them. All fields are optional.</p>
          </div>

          <!-- Location -->
          <div class="border border-theme-border rounded-2xl p-4 space-y-3">
            <div class="flex items-center justify-between mb-1">
              <p class="text-sm font-semibold text-theme-text">Your location</p>
              <button type="button" @click="showMapPicker = true" class="flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 transition">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                Pick on map
              </button>
            </div>
            <input v-model="form.street_address" type="text" placeholder="Street address (optional)" :class="inputClass()" />
            <div class="grid grid-cols-2 gap-3">
              <input v-model="form.city" type="text" placeholder="City" :class="inputClass()" />
              <input v-model="form.state" type="text" placeholder="State / Province" :class="inputClass()" />
            </div>
            <div class="grid grid-cols-2 gap-3">
              <input v-model="form.zip_code" type="text" placeholder="ZIP / Postal code" :class="inputClass()" />
              <input v-model="form.country" type="text" placeholder="Country" :class="inputClass()" />
            </div>
          </div>

          <!-- Business (lessor only) -->
          <div v-if="form.role === 'lessor'" class="border border-theme-border rounded-2xl p-4 space-y-3">
            <p class="text-sm font-semibold text-theme-text">Business details</p>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-semibold text-theme-text2 mb-1.5">Company name</label>
                <input v-model="form.company_name" type="text" placeholder="Your business (opt.)" :class="inputClass()" />
              </div>
              <div>
                <label class="block text-xs font-semibold text-theme-text2 mb-1.5">PayPal email</label>
                <input v-model="form.paypal_email" type="email" placeholder="payouts@email.com" :class="inputClass()" />
              </div>
            </div>
          </div>

          <MapPickerModal :show="showMapPicker" @close="showMapPicker = false" @confirm="onMapConfirm" />

          <button @click="nextStep" :disabled="saving" :class="btnClass(!saving)">
            <i v-if="saving" class="pi pi-spinner pi-spin mr-2"></i>
            {{ saving ? 'Saving...' : 'Continue' }}
            <svg v-if="!saving" class="w-4 h-4 inline ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>

        <!-- ─── Step 4: Documents ─── -->
        <div v-else-if="step === 4" class="space-y-5">
          <div>
            <h2 class="text-2xl font-bold text-theme-text">Identity verification</h2>
            <p class="text-sm text-theme-muted mt-1">
              Required to {{ form.role === 'lessor' ? 'list equipment' : 'rent equipment' }}. Our team reviews documents within 24 hours.
            </p>
          </div>

          <div>
            <label class="block text-sm font-semibold text-theme-text2 mb-2">Document type *</label>
            <select v-model="form.document_type" :class="inputClass()">
              <option value="">Select a document type</option>
              <option value="id">National ID</option>
              <option value="passport">Passport</option>
              <option value="dl">Driver's License</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-semibold text-theme-text2 mb-2">Identity document *</label>
            <div
              @dragover.prevent @drop.prevent="onFileDrop($event, 'identity_document')"
              @click="$refs.idInput.click()"
              :class="[
                'border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all',
                idDocFile
                  ? 'border-primary-500 bg-primary-600/10'
                  : 'border-theme-border bg-theme-bg2 hover:border-primary-400'
              ]"
            >
              <template v-if="idDocFile">
                <div class="flex items-center justify-center gap-3">
                  <div class="w-10 h-10 bg-primary-600/10 border border-theme-border rounded-xl flex items-center justify-center">
                    <svg class="w-5 h-5 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                  </div>
                  <div class="text-left">
                    <p class="text-sm font-semibold text-theme-text">{{ idDocFile.name }}</p>
                    <p class="text-xs text-primary-600 mt-0.5">Click to replace</p>
                  </div>
                </div>
              </template>
              <template v-else>
                <svg class="w-10 h-10 mx-auto mb-3 text-theme-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                <p class="text-sm font-semibold text-theme-text">Drop your file here</p>
                <p class="text-sm text-theme-muted mt-0.5">or <span class="text-primary-600 font-medium">browse files</span></p>
                <p class="text-xs text-theme-muted mt-2 opacity-60">PDF, JPG or PNG · Max 10MB</p>
              </template>
              <input ref="idInput" type="file" accept=".pdf,.jpg,.jpeg,.png" class="hidden" @change="idDocFile = $event.target.files[0]" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-semibold text-theme-text2 mb-2">
              Proof of address <span class="text-theme-muted font-normal">(optional)</span>
            </label>
            <div
              @dragover.prevent @drop.prevent="onFileDrop($event, 'proof')"
              @click="$refs.proofInput.click()"
              :class="[
                'border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all',
                proofFile
                  ? 'border-primary-500 bg-primary-600/10'
                  : 'border-theme-border bg-theme-bg2 hover:border-primary-400'
              ]"
            >
              <template v-if="proofFile">
                <div class="flex items-center justify-center gap-3">
                  <div class="w-9 h-9 bg-primary-600/10 border border-theme-border rounded-xl flex items-center justify-center">
                    <svg class="w-4 h-4 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>
                  </div>
                  <div class="text-left">
                    <p class="text-sm font-semibold text-theme-text">{{ proofFile.name }}</p>
                    <p class="text-xs text-primary-600 mt-0.5">Click to replace</p>
                  </div>
                </div>
              </template>
              <template v-else>
                <p class="text-sm text-theme-muted">Utility bill, bank statement — <span class="text-primary-600 font-medium">browse</span></p>
                <p class="text-xs text-theme-muted mt-1 opacity-60">PDF, JPG, PNG</p>
              </template>
              <input ref="proofInput" type="file" accept=".pdf,.jpg,.jpeg,.png" class="hidden" @change="proofFile = $event.target.files[0]" />
            </div>
          </div>

          <button @click="nextStep" :disabled="!isStep3Valid || saving" :class="btnClass(isStep3Valid && !saving)">
            <i v-if="saving" class="pi pi-spinner pi-spin mr-2"></i>
            {{ saving ? 'Saving...' : 'Continue' }}
            <svg v-if="!saving" class="w-4 h-4 inline ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>

        <!-- ─── Step 5: Interests ─── -->
        <div v-else-if="step === 5" class="space-y-4">
          <div>
            <h2 class="text-2xl font-bold text-theme-text">What interests you?</h2>
            <p class="text-sm text-theme-muted mt-1">Pick at least one category to personalise your experience.</p>
          </div>

          <p v-if="categoryError" class="text-red-500 text-sm mb-4 flex items-center gap-1.5">
            <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            {{ categoryError }}
          </p>

          <div v-if="loadingCategories" class="flex flex-col items-center justify-center py-14">
            <i class="pi pi-spinner pi-spin text-primary-600 text-3xl mb-3"></i>
            <p class="text-sm text-theme-muted">Loading categories...</p>
          </div>
          <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-72 overflow-y-auto pr-1">
            <button
              v-for="cat in categories" :key="cat.id"
              type="button"
              @click="toggleInterest(cat.id)"
              :class="[
                'relative flex flex-col items-center gap-2.5 p-4 rounded-2xl border-2 transition-all duration-150',
                form.interests.includes(cat.id)
                  ? 'border-primary-600 bg-primary-600/10'
                  : 'border-theme-border bg-theme-bg2 hover:border-primary-400'
              ]"
            >
              <div class="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center bg-theme-card border border-theme-border">
                <img v-if="cat.image" :src="resolveCatImage(cat.image)" class="w-full h-full object-cover" />
                <svg v-else class="w-6 h-6 text-primary-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/></svg>
              </div>
              <span class="text-xs font-semibold text-theme-text text-center leading-tight">{{ cat.name }}</span>
              <div v-if="form.interests.includes(cat.id)" class="absolute top-2 right-2 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                <svg class="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M20 6L9 17l-5-5 1.4-1.4L9 14.2 18.6 4.6z"/></svg>
              </div>
            </button>
          </div>

          <p class="text-sm text-theme-muted">
            <span class="font-semibold text-primary-600">{{ form.interests.length }}</span> selected
          </p>

          <button @click="submitOnboarding" :disabled="submitting || form.interests.length === 0" :class="btnClass(form.interests.length > 0 && !submitting)">
            <i v-if="submitting" class="pi pi-spinner pi-spin mr-2"></i>
            {{ submitting ? 'Creating your experience...' : 'Complete setup' }}
            <svg v-if="!submitting" class="w-4 h-4 inline ml-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </div>

        <!-- ─── Step 6: Done ─── -->
        <div v-else-if="step === 6" class="text-center py-4">
          <div class="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg class="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M20 6L9 17l-5-5"/></svg>
          </div>
          <h2 class="text-2xl font-bold text-theme-text mb-2">You're all set! 🎉</h2>
          <p class="text-base text-theme-muted">
            Welcome to Usenlease{{ form.first_name ? ', ' + form.first_name : '' }}.
          </p>
          <p class="text-sm text-theme-muted mt-2 mb-8">
            Our team will review your documents and verify your account shortly.
          </p>
          <button @click="goToDashboard" class="w-full bg-primary-600 hover:bg-primary-700 text-white rounded-2xl py-4 font-bold text-base transition-colors">
            Go to Dashboard →
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { useAuthStore } from '@/store/auth';
import useNotifications from '@/store/notification';
import MapPickerModal from '@/components/MapPickerModal.vue';

const router = useRouter();
const authStore = useAuthStore();
const { showNotification } = useNotifications();
const api = import.meta.env.VITE_API_BASE_URL;

const TOTAL_STEPS = 5;
const step = ref(1);
const submitting = ref(false);
const saving = ref(false);
const loadingCategories = ref(false);
const categories = ref([]);
const categoryError = ref('');
const phoneError = ref('');

const photoFile = ref(null);
const photoPreview = ref(null);
const idDocFile = ref(null);
const proofFile = ref(null);

const showMapPicker = ref(false);

const form = ref({
  role: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  username: '',
  company_name: '',
  paypal_email: '',
  street_address: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
  document_type: '',
  interests: [],
});

const onMapConfirm = (result) => {
  // Always overwrite — prefill everything returned by the map
  form.value.street_address = result.street_address || '';
  form.value.city           = result.city           || '';
  form.value.state          = result.state          || '';
  form.value.zip_code       = result.zip_code       || '';
  form.value.country        = result.country        || '';
  showMapPicker.value = false;
};

const roles = [
  { value: 'lessor', icon: '🏗️', label: 'Rent out equipment', description: 'List your equipment and earn income' },
  { value: 'lessee', icon: '🔧', label: 'Rent equipment', description: 'Find and rent equipment for your projects' },
];

onMounted(async () => {
  const savedStep = authStore.user?.onboarding_step;
  if (savedStep && savedStep > 1 && savedStep < 6) step.value = savedStep;
  if (authStore.user) {
    form.value.first_name = authStore.user.first_name || '';
    form.value.last_name  = authStore.user.last_name  || '';
    form.value.role       = authStore.user.role       || '';
  }
  await loadCategories();
});

const loadCategories = async () => {
  loadingCategories.value = true;
  try {
    const { data } = await axios.get(`${api}/api/accounts/onboard/`, { withCredentials: true });
    categories.value = data.categories || [];
    if (data.interests?.length) form.value.interests = data.interests;
  } catch { /* silent */ } finally {
    loadingCategories.value = false;
  }
};

const onPhoneInput = (number, phoneObj) => {
  form.value.phone_number = phoneObj?.valid ? phoneObj.number : number;
};

const onPhotoChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  photoFile.value = file;
  photoPreview.value = URL.createObjectURL(file);
};

const onFileDrop = (e, type) => {
  const file = e.dataTransfer.files[0];
  if (!file) return;
  if (type === 'identity_document') idDocFile.value = file;
  else proofFile.value = file;
};

const toggleInterest = (id) => {
  const idx = form.value.interests.indexOf(id);
  if (idx > -1) form.value.interests.splice(idx, 1);
  else form.value.interests.push(id);
  categoryError.value = '';
};

const resolveCatImage = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${api}/media/${path}`;
};

const inputClass = (extra = '') =>
  `w-full border border-theme-input-border rounded-xl px-4 py-3 text-base bg-theme-input text-theme-input-text placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${extra}`;

const btnClass = (enabled) =>
  `w-full rounded-2xl py-4 font-bold text-base transition-all ${
    enabled
      ? 'bg-primary-600 hover:bg-primary-700 text-white'
      : 'bg-theme-bg2 text-theme-muted cursor-not-allowed'
  }`;

const isStep2Valid = computed(() =>
  form.value.first_name.trim() &&
  form.value.last_name.trim() &&
  form.value.phone_number.trim()
);

const isStep3Valid = computed(() => form.value.document_type && idDocFile.value);

const patchStep = async (extraData = {}) => {
  const fd = new FormData();
  const payload = { ...form.value, onboarding_step: step.value, ...extraData };
  delete payload.interests;
  for (const [k, v] of Object.entries(payload)) {
    if (v !== null && v !== undefined && v !== '') fd.append(k, v);
  }
  if (photoFile.value) fd.append('image', photoFile.value);
  if (idDocFile.value && step.value === 4) fd.append('identity_document', idDocFile.value);
  if (proofFile.value && step.value === 4) fd.append('proof_of_address', proofFile.value);
  await axios.patch(`${api}/api/accounts/onboard/`, fd, { withCredentials: true });
};

const nextStep = async () => {
  if (step.value === 2) {
    phoneError.value = '';
    if (form.value.phone_number) {
      try {
        const { data } = await axios.get(`${api}/api/accounts/check-phone`, { params: { phone: form.value.phone_number } });
        if (data.exists) { phoneError.value = 'This phone number is already in use.'; return; }
      } catch { /* ignore */ }
    }
  }
  saving.value = true;
  try {
    await patchStep({ onboarding_step: step.value + 1 });
    if (authStore.user) authStore.user.onboarding_step = step.value + 1;
    await authStore.getUserData();
    step.value++;
  } catch (err) {
    showNotification('Error', err.response?.data?.detail || 'Could not save. Try again.', 'error');
  } finally {
    saving.value = false;
  }
};

const submitOnboarding = async () => {
  if (form.value.interests.length === 0) {
    categoryError.value = 'Please select at least one category.';
    return;
  }
  submitting.value = true;
  try {
    const fd = new FormData();
    fd.append('onboarding_step', '6');
    fd.append('is_onboarded', 'true');
    form.value.interests.forEach(id => fd.append('interests', id));
    await axios.patch(`${api}/api/accounts/onboard/`, fd, { withCredentials: true });
    await authStore.getUserData();
    step.value = 6;
  } catch (err) {
    showNotification('Error', err.response?.data?.detail || 'Failed to complete onboarding.', 'error');
  } finally {
    submitting.value = false;
  }
};

const goToDashboard = () => {
  router.push(form.value.role === 'lessor' ? '/new-profile/items' : '/');
};
</script>

<style scoped>
/* Override vue-tel-input using app CSS variables */
:deep(.tel-input-custom) {
  border: 1px solid var(--color-inputBorder) !important;
  border-radius: 0.75rem !important;
  background: var(--color-inputBg) !important;
  box-shadow: none !important;
  min-height: 50px;
}

:deep(.tel-input-custom):focus-within {
  border-color: var(--color-primary, #ff6f00) !important;
  box-shadow: 0 0 0 2px rgba(255, 111, 0, 0.15) !important;
}

:deep(.tel-input-custom .vti__dropdown) {
  background: transparent !important;
  border-right: 1px solid var(--color-inputBorder);
  padding: 0 10px;
  border-radius: 0.75rem 0 0 0.75rem;
}

:deep(.tel-input-custom .vti__dropdown:hover) {
  background: var(--color-bgSecondary) !important;
}

:deep(.tel-input-custom input) {
  background: transparent !important;
  color: var(--color-inputText) !important;
  font-size: 1rem !important;
  padding: 12px 14px !important;
  border: none !important;
  outline: none !important;
  width: 100%;
}

:deep(.tel-input-custom input::placeholder) {
  color: var(--color-muted, #9ca3af) !important;
}

:deep(.tel-input-custom .vti__dropdown-list) {
  background: var(--color-cardBg) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: 0.75rem !important;
  box-shadow: 0 10px 25px rgba(0,0,0,0.12) !important;
  z-index: 9999 !important;
}

:deep(.tel-input-custom .vti__dropdown-item) {
  padding: 10px 14px !important;
  font-size: 0.875rem !important;
  color: var(--color-inputText) !important;
}

:deep(.tel-input-custom .vti__dropdown-item:hover),
:deep(.tel-input-custom .vti__dropdown-item.highlighted) {
  background: var(--color-bgSecondary) !important;
}

:deep(.tel-input-custom .vti__search_box) {
  border: 1px solid var(--color-inputBorder) !important;
  border-radius: 0.5rem !important;
  padding: 8px 12px !important;
  margin: 8px !important;
  width: calc(100% - 16px) !important;
  font-size: 0.875rem !important;
  background: var(--color-inputBg) !important;
  color: var(--color-inputText) !important;
}

:deep(.tel-input-custom .vti__country-code) {
  color: var(--color-inputText) !important;
  font-size: 0.9rem !important;
  font-weight: 500 !important;
}
</style>
